import { useTranslation } from 'react-i18next';
import { scoreColor, fmtScore } from '@/utils/analytics';

export interface OuScoreRow {
  id: string;
  name: string;
  score: number;
  /** Encuestas respondidas recibidas por las personas de la unidad. */
  evaluations: number;
  peopleCount: number;
  /** Porcentaje 0–100 de omisión de la unidad; `null` cuando la unidad no tiene encuestas cerradas. */
  omissionRate: number | null;
}

interface OUDrillCardProps {
  ouScores: OuScoreRow[];
}

function omitColor(rate: number): string {
  if (rate >= 10) return 'var(--cp-danger-light)';
  if (rate >= 5) return '#f59e0b';
  return '#059669';
}

/**
 * Score por unidad organizacional.
 *
 * La tabla usa una rejilla de columnas fijas en vez de una fila flexible: con nombres de OU de
 * largo variable las celdas de puntaje y omisión se recorrían de renglón en renglón y no había
 * forma de comparar dos unidades de un vistazo.
 */
export function OUDrillCard({ ouScores }: OUDrillCardProps) {
  const { t } = useTranslation();

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-building" aria-hidden="true" />
            {t('analytics.summary.scoreByOU')}
          </div>
          <div className="au-card-subtitle">{t('analytics.summary.scoreByOUSubtitle')}</div>
        </div>
      </div>

      {ouScores.length === 0 ? (
        <div className="au-fund-state">{t('analytics.noData')}</div>
      ) : (
        <div className="au-ou-list">
          <div className="au-ou-row au-ou-head" role="row">
            <span className="au-ou-name">{t('analytics.heatmap.unitOU')}</span>
            <span className="au-ou-bar-head">{t('analytics.summary.distribution')}</span>
            <span className="au-ou-score">{t('analytics.kpi.avgScore')}</span>
            <span className="au-ou-meta au-ou-people">{t('analytics.summary.omitPeople')}</span>
            <span className="au-ou-meta">{t('analytics.kpi.evaluations')}</span>
            <span className="au-ou-omit">{t('analytics.summary.omitRate')}</span>
          </div>

          {ouScores.map((row) => (
            <div key={row.id} className="au-ou-row">
              <span className="au-ou-name" title={row.name}>{row.name}</span>
              <span className="au-ou-bar">
                <span
                  className="au-ou-bar-fill"
                  style={{ width: `${(row.score / 4) * 100}%`, background: scoreColor(row.score) }}
                />
              </span>
              <span className="au-ou-score" style={{ color: scoreColor(row.score) }}>
                {fmtScore(row.score)}
              </span>
              <span className="au-ou-meta au-ou-people">{row.peopleCount}</span>
              <span className="au-ou-meta">{row.evaluations.toLocaleString()}</span>
              <span
                className="au-ou-omit"
                style={{ color: row.omissionRate === null ? undefined : omitColor(row.omissionRate) }}
              >
                {row.omissionRate === null ? '—' : `${row.omissionRate}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
